import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  RefreshCw,
  Calendar,
  Layers,
  MessageSquare,
  Database,
  Check,
  AlertCircle,
  Zap,
  ArrowLeft,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/admin/sync")({
  component: AdminSyncPage,
});

interface BaseResult {
  ok: boolean;
  durationMs: number;
  error?: string;
}
interface PlaneMilestonesRes extends BaseResult {
  enqueued: number;
}
interface GoogleCalendarRes extends BaseResult {
  total: number;
  errors: number;
}
interface StandupRepliesRes extends BaseResult {
  scanned: number;
  newReplies: number;
}
interface PlaneCacheRes extends BaseResult {
  warmedUsers: number;
}
interface RunAllSummary {
  totalDurationMs: number;
  planeMilestones: PlaneMilestonesRes;
  googleCalendar: GoogleCalendarRes;
  standupReplies: StandupRepliesRes;
  planeCache: PlaneCacheRes;
}

// Catálogo de syncs. Cada uno tiene su endpoint, icono y describe qué
// hace para que el admin sepa cuál disparar. `summarize` extrae los
// counts relevantes del response para mostrar en la UI.
type SyncKey = "planeMilestones" | "googleCalendar" | "standupReplies" | "planeCache";
interface SyncDef {
  key: SyncKey;
  path: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  summarize: (r: unknown) => string;
}
const SYNCS: SyncDef[] = [
  {
    key: "googleCalendar",
    path: "/admin/sync/google-calendar",
    icon: <Calendar className="size-5" />,
    title: "Google Calendar",
    description:
      "Mirror del calendario de cada usuario activo del workspace (eventos, attendees, recurrencias). Default cada 30 min.",
    summarize: (r) => {
      const x = r as GoogleCalendarRes;
      return `${x.total - x.errors}/${x.total} usuarios sincronizados${x.errors > 0 ? ` · ${x.errors} con error` : ""}`;
    },
  },
  {
    key: "planeMilestones",
    path: "/admin/sync/plane-milestones",
    icon: <Layers className="size-5" />,
    title: "Plane · Milestones",
    description:
      "Encola sync de milestones/cycles para todos los proyectos linkeados a Plane. Async vía queue.",
    summarize: (r) => {
      const x = r as PlaneMilestonesRes;
      return `${x.enqueued} proyectos encolados`;
    },
  },
  {
    key: "standupReplies",
    path: "/admin/sync/standup-replies",
    icon: <MessageSquare className="size-5" />,
    title: "Replies de standups (MM)",
    description:
      "Pull del thread de cada standup posteado en los últimos 14 días en Mattermost. Trae las respuestas al historial del panel.",
    summarize: (r) => {
      const x = r as StandupRepliesRes;
      return `${x.newReplies} replies nuevas · ${x.scanned} standups escaneados`;
    },
  },
  {
    key: "planeCache",
    path: "/admin/sync/plane-cache",
    icon: <Database className="size-5" />,
    title: "Plane · Cache pre-warm",
    description:
      "Refresca el cache de tickets de cada usuario con Plane vinculado. Hace que la próxima carga del dashboard sea instantánea.",
    summarize: (r) => {
      const x = r as PlaneCacheRes;
      return `${x.warmedUsers} usuarios con cache fresca`;
    },
  },
];

function AdminSyncPage() {
  const [results, setResults] = useState<Partial<Record<SyncKey, BaseResult>>>({});

  const runAll = useMutation<RunAllSummary>({
    mutationFn: () => api.post("/admin/sync/run", {}),
    onSuccess: (summary) => {
      setResults({
        planeMilestones: summary.planeMilestones,
        googleCalendar: summary.googleCalendar,
        standupReplies: summary.standupReplies,
        planeCache: summary.planeCache,
      });
    },
  });

  const runOne = useMutation<BaseResult, Error, { key: SyncKey; path: string }>({
    mutationFn: ({ path }) => api.post(path, {}),
    onSuccess: (res, { key }) => {
      setResults((prev) => ({ ...prev, [key]: res }));
    },
  });

  const oneBusyKey = runOne.isPending ? runOne.variables?.key : undefined;
  const anyBusy = runAll.isPending || runOne.isPending;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Sincronización manual"
        subtitle="Dispara los jobs que normalmente corren cada 30 min. Útil cuando necesitas resultados inmediatos."
        actions={
          <>
            <Link to="/admin" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <button
              type="button"
              onClick={() => runAll.mutate()}
              disabled={anyBusy}
              className="btn btn-primary btn-sm"
            >
              {runAll.isPending ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                <Zap className="size-4" />
              )}
              Correr todo
            </button>
          </>
        }
      />

      {runAll.data && (
        <div className="card-elevated p-3 text-sm text-base-content/70 flex items-center gap-2">
          <Check className="size-4 text-success" />
          Todo corrió en {(runAll.data.totalDurationMs / 1000).toFixed(1)}s
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {SYNCS.map((s) => {
          const res = results[s.key];
          const busy = runAll.isPending || oneBusyKey === s.key;
          return (
            <div key={s.key} className="card-elevated p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0">
                  {s.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{s.title}</div>
                  <div className="text-sm text-base-content/65 leading-relaxed mt-0.5">
                    {s.description}
                  </div>
                </div>
              </div>

              {res && (
                <div
                  className={
                    "rounded-md border p-2.5 text-xs flex items-start gap-2 " +
                    (res.ok ? "border-success/30 bg-success/5" : "border-error/30 bg-error/5")
                  }
                >
                  {res.ok ? (
                    <Check className="size-4 text-success shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="size-4 text-error shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{res.ok ? s.summarize(res) : "Error"}</div>
                    {res.error && (
                      <div className="text-base-content/55 mt-0.5 break-all">{res.error}</div>
                    )}
                    <div className="text-base-content/45 mt-0.5">
                      {(res.durationMs / 1000).toFixed(2)}s
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={() => runOne.mutate({ key: s.key, path: s.path })}
                disabled={anyBusy}
                className="btn btn-ghost btn-sm w-full justify-center"
              >
                {busy ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <RefreshCw className="size-3.5" />
                )}
                Correr ahora
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
